import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { inwardService, productService, binService, outwardService, putawayService, companyService } from '../services/api';
import { IoMdArrowDropdown } from 'react-icons/io';
import { FiDownload } from 'react-icons/fi';
import { Table, Badge, EditIcon, DeleteIcon, FormPopup, DeletePopUp, UploadPreviewPopUp, InwardDetailsModal } from '../components/common';
import * as XLSX from 'xlsx';
import { parseFileToPreview } from '../utils/uploadPreviewParser';
import { downloadInwardUploadTemplate } from '../utils/downloadInwardUploadTemplate';
import { downloadInwardProductsTemplate } from '../utils/downloadInwardProductsTemplate';

const getStatusVariant = (status) => {
  // Status is always RECEIVED
  return 'success';
};

export function InwardManagement() {
  const [products, setProducts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingTransactionId, setEditingTransactionId] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [formData, setFormData] = useState({
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    companies: [],
    productTable: {}, // { [productId]: { selected, productDescription, productTemperature, invoiceQuantity, receivedQuantity } }
    vehicleNumber: '',
    vehicleTemperature: '',
    dockNumber: '',
    dockInTime: '',
    dockOutTime: '',
    location: '',
    batches: [],
  });
  const [currentBatch, setCurrentBatch] = useState({
    batchNumber: '',
    manufacturingDate: '',
    expiryDate: '',
    quantity: 0,
    condition: 'GOOD',
    binId: '',
  });
  const [loading, setLoading] = useState(false);
  const [bins, setBins] = useState([]);
  const [binCapacityList, setBinCapacityList] = useState([]);
  const [deletePopUpOpen, setDeletePopUpOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [outwardTransactions, setOutwardTransactions] = useState([]);
  const [putawayTasks, setPutawayTasks] = useState([]);
  const [uploadPreviewOpen, setUploadPreviewOpen] = useState(false);
  const [uploadPreviewFileName, setUploadPreviewFileName] = useState('');
  const [uploadPreviewHeaders, setUploadPreviewHeaders] = useState([]);
  const [uploadPreviewRows, setUploadPreviewRows] = useState([]);
  const [uploadSelectedFile, setUploadSelectedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [productUploadPreviewOpen, setProductUploadPreviewOpen] = useState(false);
  const [productUploadPreviewFileName, setProductUploadPreviewFileName] = useState('');
  const [productUploadPreviewHeaders, setProductUploadPreviewHeaders] = useState([]);
  const [productUploadPreviewRows, setProductUploadPreviewRows] = useState([]);
  const [productUploadSelectedFile, setProductUploadSelectedFile] = useState(null);
  const [productUploadLoading, setProductUploadLoading] = useState(false);
  const fileInputRef = useRef(null);
  const productTableFileRef = useRef(null);
  const menuRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewError, setViewError] = useState(null);
  const viewRowRef = useRef(null);

  const inwardColumns = [
    { key: 'transactionId', label: 'Transaction ID' },
    { key: 'invoiceNumber', label: 'Invoice' },
    {
      key: 'companies',
      label: 'Company',
      render: (_, row) => {
        const list = row.companies || [];
        if (!list.length) return '—';
        return list.map((c) => `${c.name} (${c.companyCode || '—'})`).join(', ');
      },
    },
    {
      key: 'productId',
      label: 'Product',
      render: (_, row) => {
        const product = row.productId || row.products?.[0];
        if (!product) return '—';
        return product.sku || product;
      },
    },
    { key: 'invoiceQuantity', label: 'Invoice Qty', align: 'right' },
    { key: 'receivedQuantity', label: 'Received Qty', align: 'right' },
    {
      key: 'inwardAgeing',
      label: 'Inward Ageing',
      align: 'center',
      render: (_, row) => {
        const days = row.inwardAgeingDays;
        if (days != null) {
          return `${days} day${days !== 1 ? 's' : ''}`;
        }
        const invDate = row.invoiceDate ? new Date(row.invoiceDate) : null;
        if (!invDate || !Number.isFinite(invDate.getTime())) return '—';
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        invDate.setHours(0, 0, 0, 0);
        const d = Math.floor((today - invDate) / (1000 * 60 * 60 * 24));
        return `${d} day${d !== 1 ? 's' : ''}`;
      },
    },
    {
      key: 'vehicleNumber',
      label: 'Vehicle',
      render: (v) => v || '—',
    },
    {
      key: 'status',
      label: 'Status',
      render: (val) => (
        <Badge variant={getStatusVariant(val)}>
          {val || 'RECEIVED'}
        </Badge>
      ),
    },
  ];

  useEffect(() => {
    fetchProducts();
    fetchCompanies();
    fetchTransactions();
    fetchBins();
    fetchBinCapacityStatus();
    fetchOutwardTransactions();
    fetchPutawayTasks();
  }, []);

  const fetchBinCapacityStatus = async () => {
    try {
      const result = await binService.getBinCapacityStatus({ status: 'ACTIVE' });
      setBinCapacityList(result.data || []);
    } catch (error) {
      console.error('Error fetching bin capacity status:', error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const result = await companyService.getCompanies({ isActive: 'true' });
      setCompanies(result.data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => fetchTransactions(), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const fetchBins = async () => {
    try {
      const result = await binService.getBins();
      setBins(result.data || []);
    } catch (error) {
      console.error('Error fetching bins:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const result = await productService.getProducts();
      setProducts(result.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm && searchTerm.trim()) params.search = searchTerm.trim();
      const result = await inwardService.getInwardTransactions(params);
      setTransactions(result.data || []);
    } catch (error) {
      console.error('Error fetching inward transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOutwardTransactions = async () => {
    try {
      const result = await outwardService.getOutwardTransactions({});
      setOutwardTransactions(result.data || []);
    } catch (error) {
      console.error('Error fetching outward transactions:', error);
    }
  };

  const fetchPutawayTasks = async () => {
    try {
      const result = await putawayService.getPutawayTasks({});
      setPutawayTasks(result.data || []);
    } catch (error) {
      console.error('Error fetching putaway tasks:', error);
    }
  };

  // Check if transaction can be edited/deleted
  const canEditOrDelete = (transaction) => {
    const batchNumbers = (transaction.batches || []).map(b => b.batchNumber).filter(Boolean);
    if (batchNumbers.length === 0) return true;
    
    // Check outward transactions
    const hasOutwardUsage = outwardTransactions.some(outwardTxn => {
      if (outwardTxn.items && outwardTxn.items.length > 0) {
        return outwardTxn.items.some(item => {
          if (item.batchDetails && item.batchDetails.length > 0) {
            return item.batchDetails.some(batchDetail => {
              return batchNumbers.includes(String(batchDetail.batchNumber || ''));
            });
          }
          return false;
        });
      }
      return false;
    });
    
    // Check putaway tasks
    const transactionId = transaction.transactionId || transaction._id;
    const hasPutawayUsage = putawayTasks.some(putaway => {
      return String(putaway.transactionId) === String(transactionId) && 
             String(putaway.transactionType) === 'INWARD';
    });
    
    return !hasOutwardUsage && !hasPutawayUsage;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'invoiceQuantity' || name === 'receivedQuantity' ? parseInt(value) || 0 : value,
    });
  };

  const handleBatchChange = (e) => {
    const { name, value } = e.target;
    const updatedBatch = {
      ...currentBatch,
      [name]: name === 'quantity' ? parseInt(value) || 0 : value,
    };
    
    // If condition changes, reset binId and filter bins accordingly
    if (name === 'condition') {
      updatedBatch.binId = ''; // Reset bin selection when condition changes
    }
    
    setCurrentBatch(updatedBatch);
  };

  const productTable = formData.productTable || {};
  const selectedProductRows = (products || []).filter((p) => {
    const row = productTable[p._id];
    return row && row.selected;
  });
  const receivedQty = selectedProductRows.reduce((sum, p) => sum + (Number(productTable[p._id]?.receivedQuantity) || 0), 0);
  const totalBatchQty = (formData.batches || []).reduce((sum, b) => sum + (b.quantity || 0), 0);
  const batchesMatchReceived = receivedQty > 0 && totalBatchQty === receivedQty;
  const showAddBatchForm = !editingTransactionId && receivedQty > 0 && totalBatchQty < receivedQty;

  const setProductTableRow = (productId, patch) => {
    setFormData((prev) => ({
      ...prev,
      productTable: {
        ...prev.productTable,
        [productId]: { ...(prev.productTable?.[productId] || {}), ...patch },
      },
    }));
  };
  const setProductTableSelectAll = (selected) => {
    setFormData((prev) => {
      const next = { ...prev.productTable };
      (products || []).forEach((p) => {
        const id = p._id;
        next[id] = { ...(next[id] || {}), selected, productDescription: next[id]?.productDescription ?? p.description ?? '', productTemperature: next[id]?.productTemperature ?? '', invoiceQuantity: next[id]?.invoiceQuantity ?? 0, receivedQuantity: next[id]?.receivedQuantity ?? 0 };
      });
      return { ...prev, productTable: next };
    });
  };

  const toastOpt = {};

  const resetForm = () => {
    setFormData({
      invoiceNumber: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      companies: [],
      productTable: {},
      vehicleNumber: '',
      vehicleTemperature: '',
      dockNumber: '',
      dockInTime: '',
      dockOutTime: '',
      location: '',
      batches: [],
    });
    setCurrentBatch({
      manufacturingDate: '',
      expiryDate: '',
      quantity: 0,
      condition: 'GOOD',
      binId: '',
    });
    setEditingTransactionId(null);
  };

  const addBatch = () => {
    if (!receivedQty || receivedQty <= 0) {
      toast.error('Please enter received quantity first', toastOpt);
      return;
    }
    if (!currentBatch.batchNumber || !currentBatch.batchNumber.trim()) {
      toast.error('Please enter batch number', toastOpt);
      return;
    }
    if (!currentBatch.manufacturingDate) {
      toast.error('Please enter manufacturing date', toastOpt);
      return;
    }
    if (!currentBatch.expiryDate) {
      toast.error('Please enter expiry date', toastOpt);
      return;
    }
    if (!currentBatch.quantity || currentBatch.quantity <= 0) {
      toast.error('Please enter a quantity greater than 0', toastOpt);
      return;
    }
    if (!currentBatch.binId) {
      toast.error('Please select a bin for this batch', toastOpt);
      return;
    }

    const selectedBinCap = binCapacityList.find(
      (b) => String(b.binId || b._id) === String(currentBatch.binId)
    );
    if (selectedBinCap) {
      const qtyAlreadyInBin = (formData.batches || [])
        .filter((b) => String(b.binId) === String(currentBatch.binId))
        .reduce((sum, b) => sum + (b.quantity || 0), 0);
      const avail = Number(selectedBinCap.availableCapacity ?? 0) - qtyAlreadyInBin;
      if (currentBatch.quantity > avail) {
        toast.error(
          "Selected bin doesn't have enough available capacity. Choose another bin.",
          toastOpt
        );
        return;
      }
    }

    const newTotal = totalBatchQty + currentBatch.quantity;
    if (newTotal > receivedQty) {
      const remaining = receivedQty - totalBatchQty;
      toast.error(`Total cannot exceed received quantity (${receivedQty}). You need ${remaining} more to match. Current total: ${totalBatchQty}, adding: ${currentBatch.quantity}`, toastOpt);
      return;
    }

    setFormData({
      ...formData,
      batches: [...(formData.batches || []), { ...currentBatch }],
    });

    setCurrentBatch({
      batchNumber: '',
      manufacturingDate: '',
      expiryDate: '',
      quantity: 0,
      condition: 'GOOD',
      binId: '',
    });
  };

  const removeBatch = (index) => {
    setFormData({
      ...formData,
      batches: (formData.batches || []).filter((_, i) => i !== index),
    });
  };

  const updateBatchField = (index, field, value) => {
    const next = (formData.batches || []).map((b, i) => {
      if (i === index) {
        const updatedBatch = { ...b, [field]: field === 'quantity' ? (parseInt(value, 10) || 0) : value };
        // If condition changes, reset binId
        if (field === 'condition') {
          updatedBatch.binId = '';
        }
        return updatedBatch;
      }
      return b;
    });
    setFormData({ ...formData, batches: next });
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    const dataToSubmit = e && e.preventDefault ? formData : e;
    const pt = dataToSubmit.productTable || {};
    const selectedItems = (products || []).filter((p) => pt[p._id]?.selected).map((p) => {
      const row = pt[p._id] || {};
      return {
        productId: p._id,
        productDescription: row.productDescription ?? p.description ?? '',
        productTemperature: row.productTemperature,
        invoiceQuantity: Number(row.invoiceQuantity) || 0,
        receivedQuantity: Number(row.receivedQuantity) || 0,
      };
    });

    if (selectedItems.length === 0) {
      toast.error('Select at least one product', toastOpt);
      return;
    }
    for (const it of selectedItems) {
      if (!(it.productDescription != null && String(it.productDescription).trim())) {
        toast.error('Product Description is required for each selected row', toastOpt);
        return;
      }
      if (it.productTemperature == null || it.productTemperature === '') {
        toast.error('Product Temperature is required for each selected row', toastOpt);
        return;
      }
      if (!it.invoiceQuantity || it.invoiceQuantity <= 0) {
        toast.error('Invoice Qty must be > 0 for each selected row', toastOpt);
        return;
      }
      if (!it.receivedQuantity || it.receivedQuantity <= 0) {
        toast.error('Received Qty must be > 0 for each selected row', toastOpt);
        return;
      }
      if (it.receivedQuantity > it.invoiceQuantity) {
        toast.error('Received Qty cannot exceed Invoice Qty for each selected row', toastOpt);
        return;
      }
    }

    const submitData = {
      ...dataToSubmit,
      companies: Array.isArray(dataToSubmit.companies) ? dataToSubmit.companies : [],
      items: selectedItems,
    };
    delete submitData.company;
    delete submitData.products;
    delete submitData.productId;
    delete submitData.productTable;

    if (!submitData.invoiceNumber) {
      toast.error('Please enter invoice number', toastOpt);
      return;
    }

    const submitReceivedQty = selectedItems.reduce((s, i) => s + (i.receivedQuantity || 0), 0);
    const submitTotalBatchQty = (submitData.batches || []).reduce((sum, b) => sum + (b.quantity || 0), 0);
    if (submitTotalBatchQty !== submitReceivedQty) {
      toast.error(`Total batch quantity must equal received total (${submitReceivedQty}). Current: ${submitTotalBatchQty}`, toastOpt);
      return;
    }
    if (!submitData.batches || submitData.batches.length === 0) {
      toast.error('Please add at least one batch', toastOpt);
      return;
    }

    // Capacity check: per-bin total must not exceed available capacity
    const byBinId = {};
    for (const b of submitData.batches) {
      const id = b.binId ? String(b.binId) : '';
      if (!id) continue;
      byBinId[id] = (byBinId[id] || 0) + (Number(b.quantity) || 0);
    }
    for (const [binId, totalQty] of Object.entries(byBinId)) {
      const bin = binCapacityList.find((b) => String(b.binId || b._id) === binId);
      if (bin && totalQty > Number(bin.availableCapacity ?? 0)) {
        toast.error("Selected bin doesn't have enough available capacity. Choose another bin.", toastOpt);
        return;
      }
    }

    try {
      if (editingTransactionId) {
        await inwardService.updateInwardTransaction(editingTransactionId, submitData);
        toast.success('Inward transaction updated successfully');
      } else {
        await inwardService.createInwardTransaction(submitData);
        toast.success('Inward transaction created successfully!');
      }
      resetForm();
      setShowForm(false);
      fetchTransactions();
    } catch (error) {
      console.error('Error saving inward transaction:', error);
      const errorMsg = error?.message || (editingTransactionId ? 'Failed to update inward transaction' : 'Failed to create inward transaction');
      toast.error(`Error: ${errorMsg}`);
    }
  };

  const inwardFields = [
    // ROW 1 (5 fields)
    {
      name: 'invoiceNumber',
      label: 'Invoice Number',
      type: 'text',
      required: true,
      grid: '1',
      placeholder: 'Enter invoice no',
    },
    {
      name: 'invoiceDate',
      label: 'Invoice Date',
      type: 'date',
      required: true,
      grid: '1',
    },
    {
      name: 'companies',
      label: 'Company',
      type: 'multiselect',
      placeholder: '-- Select Company --',
      options: companies.map((c) => ({
        label: `${c.companyCode} - ${c.name}`,
        value: c._id || c.id,
      })),
      grid: '1',
    },
    {
      name: 'vehicleNumber',
      label: 'Vehicle No',
      type: 'text',
      grid: '1',
      placeholder: 'Enter vehicle no',
    },
    {
      name: 'vehicleTemperature',
      label: 'Vehicle Temperature',
      type: 'number',
      placeholder: 'Enter temperature',
      grid: '1',
    },
    // ROW 2 (5 fields)
    {
      name: 'dockNumber',
      label: 'Dock Number',
      type: 'text',
      placeholder: 'Enter dock number',
      grid: '1',
    },
    {
      name: 'dockInTime',
      label: 'Dock In-Time',
      type: 'datetime-local',
      grid: '1',
    },
    {
      name: 'dockOutTime',
      label: 'Dock Out-Time',
      type: 'datetime-local',
      grid: '1',
    },
    {
      name: 'location',
      label: 'Location',
      type: 'text',
      placeholder: 'Enter Location',
      grid: '1',
    },
  ];

  const openView = async (row) => {
    viewRowRef.current = row;
    setViewOpen(true);
    setViewLoading(true);
    setViewError(null);
    try {
      const res = await inwardService.getInwardTransactionById(row._id || row.id);
      const transaction = res.data || res;
      setViewData(transaction);
    } catch (err) {
      console.error('Error fetching inward details:', err);
      setViewError(err?.message || 'Failed to load transaction details');
      setViewData(null);
    } finally {
      setViewLoading(false);
    }
  };

  const handleEdit = async (row) => {
    try {
      const transactionDetails = await inwardService.getInwardTransactionById(row._id || row.id);
      const transaction = transactionDetails.data || transactionDetails;

      const formattedBatches = (transaction.batches || []).map((batch) => ({
        batchNumber: batch.batchNumber ?? batch.batch_number ?? '',
        manufacturingDate: batch.manufacturingDate ? new Date(batch.manufacturingDate).toISOString().split('T')[0] : '',
        expiryDate: batch.expiryDate ? new Date(batch.expiryDate).toISOString().split('T')[0] : '',
        quantity: batch.quantity || 0,
        condition: batch.condition || 'GOOD',
        binId: batch.binId?._id || batch.binId || '',
      }));

      let productTable = {};
      if (transaction.items && transaction.items.length > 0) {
        const byId = {};
        (products || []).forEach((p) => {
          const it = transaction.items.find((i) => String(i.productId?._id || i.productId) === String(p._id));
          byId[p._id] = it
            ? { selected: true, productDescription: it.productDescription ?? p.description ?? '', productTemperature: it.productTemperature != null ? it.productTemperature : '', invoiceQuantity: it.invoiceQuantity || 0, receivedQuantity: it.receivedQuantity || 0 }
            : { selected: false, productDescription: p.description || '', productTemperature: '', invoiceQuantity: 0, receivedQuantity: 0 };
        });
        productTable = byId;
      } else {
        const firstId = transaction.products?.[0]?._id || transaction.products?.[0];
        (products || []).forEach((p) => {
          const selected = firstId && String(p._id) === String(firstId);
          productTable[p._id] = selected
            ? { selected: true, productDescription: transaction.productDescription || p.description || '', productTemperature: transaction.productTemperature != null ? transaction.productTemperature : '', invoiceQuantity: transaction.invoiceQuantity || 0, receivedQuantity: transaction.receivedQuantity || 0 }
            : { selected: false, productDescription: p.description || '', productTemperature: '', invoiceQuantity: 0, receivedQuantity: 0 };
        });
      }

      setFormData({
        invoiceNumber: transaction.invoiceNumber || '',
        invoiceDate: transaction.invoiceDate ? new Date(transaction.invoiceDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        companies: (transaction.companies || []).map((c) => c._id || c.id),
        productTable,
        vehicleNumber: transaction.vehicleNumber || '',
        vehicleTemperature: transaction.vehicleTemperature != null ? transaction.vehicleTemperature : '',
        dockNumber: transaction.dockNumber || '',
        dockInTime: transaction.dockInTime ? new Date(transaction.dockInTime).toISOString().slice(0, 16) : '',
        dockOutTime: transaction.dockOutTime ? new Date(transaction.dockOutTime).toISOString().slice(0, 16) : '',
        location: transaction.location || '',
        batches: formattedBatches,
      });
      setEditingTransactionId(transaction._id || row._id || row.id);
      setShowForm(true);
    } catch (error) {
      console.error('Error fetching transaction details:', error);
      toast.error('Failed to load transaction details', toastOpt);
    }
  };

  const handleDelete = (row) => {
    setRowToDelete(row);
    setDeletePopUpOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!rowToDelete) return;
    const id = rowToDelete._id || rowToDelete.id;
    try {
      setDeleteLoading(true);
      await inwardService.deleteInwardTransaction(id);
      setDeletePopUpOpen(false);
      setRowToDelete(null);
      fetchTransactions();
      // Use default toast container
      toast.success('Inward transaction deleted successfully');
    } catch (err) {
      console.error('Error deleting inward transaction:', err);
      // Use default toast container
      toast.error(err?.message || 'Failed to delete inward transaction');
    } finally {
      setDeleteLoading(false);
    }
  };

  const resetUploadPreview = () => {
    setUploadPreviewOpen(false);
    setUploadPreviewFileName('');
    setUploadPreviewHeaders([]);
    setUploadPreviewRows([]);
    setUploadSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUploadInwardClick = () => {
    fileInputRef.current?.click();
  };

  const handleUploadFileSelect = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    try {
      const { headers, rows, fileName } = await parseFileToPreview(file);
      setUploadPreviewFileName(fileName);
      setUploadPreviewHeaders(headers);
      setUploadPreviewRows(rows);
      setUploadSelectedFile(file);
      setUploadPreviewOpen(true);
    } catch (err) {
      toast.error(err?.message || 'Failed to read file');
    }
  };

  const handleUploadApply = async () => {
    if (!uploadSelectedFile) {
      resetUploadPreview();
      return;
    }
    setUploadLoading(true);
    try {
      const res = await inwardService.bulkUploadInwardExcel(uploadSelectedFile);
      const created = res?.data?.created ?? 0;
      const errors = res?.data?.errors || [];
      const errCount = errors.length;

      if (created > 0 && errCount === 0) {
        toast.success(`✅ Success! ${created} inward transaction(s) uploaded successfully.`, {
          autoClose: 4000,
        });
      } else if (created > 0 && errCount > 0) {
        toast.success(`✅ ${created} inward transaction(s) uploaded successfully.`, {
          autoClose: 3000,
        });
        // Show errors separately after a short delay - show exact backend error messages
        setTimeout(() => {
          const errorMessages = errors.slice(0, 5).map((e) => {
            // Show exact backend error message - no modification
            const backendError = e.error || (typeof e === 'string' ? e : '');
            return backendError;
          }).filter(Boolean);
          const errorText = errCount <= 5 
            ? errorMessages.join('. ') 
            : `${errorMessages.join('. ')}. ...and ${errCount - 5} more error(s).`;
          // Show exact backend error message
          toast.error(errorText || 'Some rows had errors', {
            autoClose: 6000,
          });
        }, 500);
      } else if (errCount > 0) {
        // All failed - show exact backend error messages
        const errorMessages = errors.slice(0, 5).map((e) => {
          // Show exact backend error message - no modification
          const backendError = e.error || (typeof e === 'string' ? e : '');
          return backendError;
        }).filter(Boolean);
        const errorText = errCount <= 5 
          ? errorMessages.join('. ') 
          : `${errorMessages.join('. ')}. ...and ${errCount - 5} more error(s).`;
        // Show exact backend error message
        toast.error(errorText || 'Upload failed', {
          autoClose: 7000,
        });
      } else {
        toast.info('ℹ️ No transactions found in file. Please check your file and try again.', {
          autoClose: 4000,
        });
      }
      fetchTransactions();
      // Delay closing popup slightly so user can see toast
      setTimeout(() => {
        resetUploadPreview();
      }, 500);
    } catch (err) {
      // Extract backend error messages from error response data
      let errorMsg = err?.message || 'Upload failed';
      
      // Check if error has responseData attached (from API service)
      if (err?.responseData) {
        const errorData = err.responseData;
        // Check for errors array in data.errors or errors
        if (errorData?.data?.errors && Array.isArray(errorData.data.errors) && errorData.data.errors.length > 0) {
          // Extract exact backend error messages
          const backendErrors = errorData.data.errors.slice(0, 5).map((e) => e.error || '').filter(Boolean);
          if (backendErrors.length > 0) {
            errorMsg = backendErrors.join('. ');
          }
        } else if (errorData?.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          const backendErrors = errorData.errors.slice(0, 5).map((e) => e.error || '').filter(Boolean);
          if (backendErrors.length > 0) {
            errorMsg = backendErrors.join('. ');
          }
        }
      }
      
      toast.error(errorMsg, {
        autoClose: 5000,
      });
      // Delay closing popup slightly so user can see toast
      setTimeout(() => {
        resetUploadPreview();
      }, 500);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDownloadFormat = () => {
    downloadInwardUploadTemplate();
    toast.success('Inward format downloaded. Fill and upload to add inward transactions.');
  };

  const handleExportProductsTemplate = () => {
    if (!products || products.length === 0) {
      toast.error('No products available to export', toastOpt);
      return;
    }
    downloadInwardProductsTemplate(products);
    toast.success('Products template downloaded. Fill editable columns and upload to pre-fill table.', toastOpt);
  };

  const resetProductUploadPreview = () => {
    setProductUploadPreviewOpen(false);
    setProductUploadPreviewFileName('');
    setProductUploadPreviewHeaders([]);
    setProductUploadPreviewRows([]);
    setProductUploadSelectedFile(null);
    if (productTableFileRef.current) productTableFileRef.current.value = '';
  };

  const handleProductUploadFileSelect = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    try {
      const { headers, rows, fileName } = await parseFileToPreview(file);
      setProductUploadPreviewFileName(fileName);
      setProductUploadPreviewHeaders(headers);
      setProductUploadPreviewRows(rows);
      setProductUploadSelectedFile(file);
      setProductUploadPreviewOpen(true);
    } catch (err) {
      toast.error(err?.message || 'Failed to read file', toastOpt);
    }
  };

  const handleProductUploadApply = async () => {
    if (!productUploadSelectedFile) {
      resetProductUploadPreview();
      return;
    }
    setProductUploadLoading(true);
    try {
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(new Uint8Array(ev.target.result));
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsArrayBuffer(productUploadSelectedFile);
      });
      const wb = XLSX.read(data, { type: 'array' });
      const firstSheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: '' });
      if (rows.length < 2) {
        toast.error('File must have header row and at least one data row', toastOpt);
        return false;
      }
      const headers = (rows[0] || []).map((h) => String(h).trim().toLowerCase().replace(/\s+/g, ''));
      const getCol = (names) => {
        for (const n of names) {
          const i = headers.findIndex((h) => h === n || h === String(n).toLowerCase());
          if (i >= 0) return i;
        }
        return -1;
      };
      const skuCol = getCol(['productsku', 'product_sku', 'sku']);
      const codeCol = getCol(['productcode', 'product_code', 'code']);
      if (skuCol < 0 && codeCol < 0) {
        toast.error('File must have productSKU or productCode column', toastOpt);
        return false;
      }
      const descCol = getCol(['productdescription', 'product_description']);
      const tempCol = getCol(['producttemperature', 'product_temperature']);
      const invCol = getCol(['invoicequantity', 'invoice_quantity']);
      const recCol = getCol(['receivedquantity', 'received_quantity']);

      const errors = [];
      const updates = {};
      for (let r = 1; r < rows.length; r++) {
        const row = rows[r] || [];
        const rawSku = skuCol >= 0 ? String(row[skuCol] ?? '').trim() : '';
        const rawCode = codeCol >= 0 ? String(row[codeCol] ?? '').trim() : '';
        const key = rawSku || rawCode;
        if (!key) continue;
        const product = products.find(
          (p) =>
            (rawSku && (p.sku || '').toString().trim() === rawSku) ||
            (rawCode && ((p.productCode ?? p.sku) || '').toString().trim() === rawCode)
        );
        if (!product) {
          errors.push({ rowIndex: r + 1, error: `Unknown product SKU/Code: ${key}` });
          continue;
        }
        const invQ = invCol >= 0 ? Number(row[invCol]) : 0;
        const recQ = recCol >= 0 ? Number(row[recCol]) : 0;
        if (!Number.isFinite(invQ) || invQ < 1) {
          errors.push({ rowIndex: r + 1, error: `Invalid invoiceQuantity for ${key}` });
          continue;
        }
        if (!Number.isFinite(recQ) || recQ < 1 || recQ > invQ) {
          errors.push({ rowIndex: r + 1, error: `Invalid receivedQuantity for ${key} (must be > 0 and <= invoiceQuantity)` });
          continue;
        }
        const productDescription = descCol >= 0 ? String(row[descCol] ?? '').trim() : (product.description || '');
        const productTemperature = tempCol >= 0 ? (row[tempCol] !== '' && row[tempCol] != null ? Number(row[tempCol]) : '') : '';
        if (!(productDescription || product.description || '').trim()) {
          errors.push({ rowIndex: r + 1, error: `Product description required for ${key}` });
          continue;
        }
        if (productTemperature === '' || productTemperature == null || !Number.isFinite(Number(productTemperature))) {
          errors.push({ rowIndex: r + 1, error: `Product temperature required for ${key}` });
          continue;
        }
        updates[product._id] = {
          selected: true,
          productDescription: productDescription || product.description || '',
          productTemperature: Number.isFinite(Number(productTemperature)) ? Number(productTemperature) : '',
          invoiceQuantity: invQ,
          receivedQuantity: recQ,
        };
      }
      if (errors.length > 0) {
        const msg = errors.slice(0, 5).map((e) => `Row ${e.rowIndex}: ${e.error}`).join('; ');
        toast.error(errors.length > 5 ? `${msg}... and ${errors.length - 5} more` : msg, { autoClose: 6000 });
        return false;
      }
      setFormData((prev) => ({
        ...prev,
        productTable: { ...prev.productTable, ...updates },
      }));
      if (Object.keys(updates).length > 0) {
        toast.success(`${Object.keys(updates).length} product row(s) filled and selected`, toastOpt);
      }
      resetProductUploadPreview();
    } catch (err) {
      toast.error(err?.message || 'Failed to parse file', toastOpt);
      return false;
    } finally {
      setProductUploadLoading(false);
    }
  };

  const handleDownloadGRN = async (row) => {
    try {
      const id = row._id || row.id;
      const blob = await inwardService.downloadInwardGRN(id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `GRN_${row.transactionId || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download GRN');
    }
  };

  const inwardActions = (row) => {
    const canEdit = canEditOrDelete(row);
    const actions = [];
    if (canEdit) {
      actions.push({ label: 'Edit', icon: EditIcon, onClick: (r) => handleEdit(r) });
      actions.push({ label: 'Delete', icon: DeleteIcon, onClick: (r) => handleDelete(r) });
    }
    actions.push({ label: 'Export GRN', icon: FiDownload, onClick: (r) => handleDownloadGRN(r) });
    return actions;
  };

  const handleExportInwardExcel = async () => {
    setExportLoading(true);
    try {
      const params = {};
      if (searchTerm && String(searchTerm).trim()) params.search = String(searchTerm).trim();
      const blob = await inwardService.downloadInwardExcel(params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `inward_report_${Date.now()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to export inward excel");
    } finally {
      setExportLoading(false);
    }
  };
 
 const handleExportInwardGRN = () => {
   // ✅ UI only for now (later backend connect)
   toast.info("GRN export will be added soon.");
 };

  return (
    <div className="page-container">
   <div className="mb-6 w-full">
  <div
    className="flex items-center justify-between w-full flex-wrap gap-4"
    style={{
      backgroundColor: '#FFFFFF',
      border: '1px solid #EEF1F5',
      borderRadius: 18,
      padding: '16px 18px',
    }}
  >
    <h1 style={{ fontSize: 20, fontWeight: 600, color: '#101828', margin: 0 }}>
      Inward Management
    </h1>

    <div className="flex items-center gap-[14px] flex-wrap">
      <div className="relative" style={{ width: 320 }}>
        <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-4 w-4">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.3-4.3m1.8-5.2a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </span>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by ID"
          className="w-full rounded-[12px] border border-[#D0D5DD] bg-white pl-10 pr-4 text-sm text-gray-800 placeholder:text-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
          style={{ height: 44 }}
        />
      </div>

      <button
        type="button"
        onClick={() => {
          resetForm();
          setShowForm(true);
        }}
        className="shrink-0 rounded-[12px] bg-[#188742] text-white font-semibold transition hover:bg-[#15733a] active:scale-[0.98]"
        style={{ height: 44, paddingLeft: 18, paddingRight: 18 }}
      >
        Create Inward
      </button>

      <div className="relative shrink-0" ref={menuRef}>
        <button
          type="button"
          onClick={() => setMenuOpen((o) => !o)}
          className="flex items-center justify-center rounded-[12px] border-[1.5px] border-[#188742] bg-white text-[#188742] transition hover:bg-[rgba(24,135,66,0.06)]"
          style={{ width: 44, height: 44 }}
          aria-label="More options"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
            <circle cx="12" cy="6" r="1.5" />
            <circle cx="12" cy="12" r="1.5" />
            <circle cx="12" cy="18" r="1.5" />
          </svg>
        </button>

        {menuOpen && (
          <div
            className="absolute right-0 top-full z-50 mt-1 rounded-[12px] border border-[#EEF1F5] bg-white shadow-lg overflow-hidden"
            style={{ width: 240, padding: 8 }}
          >
            <button
              type="button"
              onClick={() => {
                handleDownloadFormat();
                setMenuOpen(false);
              }}
              className="w-full text-left rounded-[10px] px-3 text-[#101828] font-medium transition hover:bg-[#F6F7FA]"
              style={{ height: 40 }}
            >
              Download Format
            </button>
            <button
              type="button"
              onClick={() => {
                handleUploadInwardClick();
                setMenuOpen(false);
              }}
              className="w-full text-left rounded-[10px] px-3 text-[#101828] font-medium transition hover:bg-[#F6F7FA]"
              style={{ height: 40 }}
            >
              Upload Inward
            </button>
            <button
              type="button"
              onClick={() => {
                handleExportInwardExcel();
                setMenuOpen(false);
              }}
              disabled={exportLoading}
              className="w-full text-left rounded-[10px] px-3 text-[#101828] font-medium transition hover:bg-[#F6F7FA] disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ height: 40 }}
            >
              {exportLoading ? 'Exporting...' : 'Export Excel'}
            </button>
          </div>
        )}
      </div>
    </div>
  </div>
</div>

      <input
        type="file"
        ref={fileInputRef}
        accept=".xlsx,.xls,.csv"
        className="hidden"
        onChange={handleUploadFileSelect}
      />


      <UploadPreviewPopUp
        open={uploadPreviewOpen}
        onClose={resetUploadPreview}
        title="Upload Inward - File overview"
        fileName={uploadPreviewFileName}
        previewHeaders={uploadPreviewHeaders}
        previewRows={uploadPreviewRows}
        onApply={handleUploadApply}
        onCancel={resetUploadPreview}
        loading={uploadLoading}
      />
      <UploadPreviewPopUp
        open={productUploadPreviewOpen}
        onClose={resetProductUploadPreview}
        title="Upload Products Excel - File overview"
        fileName={productUploadPreviewFileName}
        previewHeaders={productUploadPreviewHeaders}
        previewRows={productUploadPreviewRows}
        onApply={handleProductUploadApply}
        onCancel={resetProductUploadPreview}
        loading={productUploadLoading}
      />
      <DeletePopUp
        open={deletePopUpOpen}
        onClose={() => { setDeletePopUpOpen(false); setRowToDelete(null); }}
        onConfirm={handleDeleteConfirm}
        entityLabel="inward transaction"
      />
      <FormPopup
        open={showForm}
        title={editingTransactionId ? "Edit Inward Transaction" : "Create Inward Transaction"}
        onClose={() => {
          resetForm();
          setShowForm(false);
          if (productUploadPreviewOpen) resetProductUploadPreview();
        }}
        onSubmit={handleSubmit}
        submitText={editingTransactionId ? "Update" : "Create"}
        values={formData}
        setValues={setFormData}
        fields={inwardFields}
        width="xl"
      >
        <div
          style={{
            border: '1px solid #E4E7EC',
            borderRadius: 14,
            backgroundColor: '#FFFFFF',
            padding: 18,
            marginTop: 18,
          }}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#101828', margin: 0 }}>Products</h3>
            <div className="flex flex-wrap items-center gap-[14px]">
              <button
                type="button"
                onClick={() => setProductTableSelectAll(true)}
                className="rounded-[10px] border-[1.5px] border-[#188742] bg-white text-[#188742] font-semibold transition hover:bg-[#188742]/5"
                style={{ height: 40, paddingLeft: 18, paddingRight: 18 }}
              >
                Select all
              </button>
              <button
                type="button"
                onClick={handleExportProductsTemplate}
                className="rounded-[10px] border-[1.5px] border-[#188742] bg-white text-[#188742] font-semibold transition hover:bg-[#188742]/5"
                style={{ height: 40, paddingLeft: 18, paddingRight: 18 }}
              >
                Export CSV
              </button>
              <button
                type="button"
                onClick={() => productTableFileRef.current?.click()}
                className="rounded-[10px] border-[1.5px] border-[#188742] bg-white text-[#188742] font-semibold transition hover:bg-[#188742]/5"
                style={{ height: 40, paddingLeft: 18, paddingRight: 18 }}
              >
                Upload Product CSV
              </button>
            </div>
          </div>
          <input
            type="file"
            ref={productTableFileRef}
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={handleProductUploadFileSelect}
          />
          {productUploadPreviewOpen ? (
            <p className="text-sm text-gray-500 py-4">Previewing upload — use Apply or Cancel in the popup.</p>
          ) : (
          <div
            className="overflow-x-auto overflow-y-auto"
            style={{ maxHeight: 280, marginTop: 14, border: '1px solid #EEF1F5', borderRadius: 14, padding: 14, backgroundColor: '#fff' }}
          >
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0">
                <tr>
                  <th className="px-3 text-left font-semibold w-10 rounded-l-[12px]" style={{ height: 52, backgroundColor: '#F6F7FA', color: '#98A2B3' }}>Select</th>
                  <th className="px-3 text-left font-semibold" style={{ backgroundColor: '#F6F7FA', color: '#98A2B3' }}>Product Name</th>
                  <th className="px-3 text-left font-semibold" style={{ backgroundColor: '#F6F7FA', color: '#98A2B3' }}>SKU</th>
                  <th className="px-3 text-left font-semibold" style={{ backgroundColor: '#F6F7FA', color: '#98A2B3' }}>Product Code</th>
                  <th className="px-3 text-left font-semibold" style={{ backgroundColor: '#F6F7FA', color: '#98A2B3' }}>Product Description</th>
                  <th className="px-3 text-left font-semibold" style={{ backgroundColor: '#F6F7FA', color: '#98A2B3' }}>Product Temp</th>
                  <th className="px-3 text-left font-semibold" style={{ backgroundColor: '#F6F7FA', color: '#98A2B3' }}>Invoice Qty</th>
                  <th className="px-3 text-left font-semibold rounded-r-[12px]" style={{ backgroundColor: '#F6F7FA', color: '#98A2B3' }}>Received Qty</th>
                </tr>
              </thead>
              <tbody>
                {(products || []).map((p) => {
                  const id = p._id;
                  const row = productTable[id] || { selected: false, productDescription: p.description || '', productTemperature: '', invoiceQuantity: 0, receivedQuantity: 0 };
                  const disabled = !row.selected;
                  return (
                    <tr key={id} className={`border-b border-gray-100 ${disabled ? 'opacity-60 bg-gray-50/50' : 'hover:bg-gray-50/50'}`}>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={!!row.selected}
                          onChange={(e) => setProductTableRow(id, { ...row, selected: e.target.checked })}
                          className="h-4 w-4 rounded border-gray-300 text-emerald-600"
                        />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap" style={{ color: '#101828' }}>{(p.skuName || p.description || '').slice(0, 40)}</td>
                      <td className="px-3 py-2 whitespace-nowrap" style={{ color: '#101828' }}>{p.sku || ''}</td>
                      <td className="px-3 py-2 whitespace-nowrap" style={{ color: '#101828' }}>{p.productCode ?? p.sku ?? ''}</td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          disabled={disabled}
                          value={row.productDescription ?? ''}
                          onChange={(e) => setProductTableRow(id, { ...row, productDescription: e.target.value })}
                          className={`w-full min-w-[120px] rounded-[10px] border border-[#D0D5DD] bg-white focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)] ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'text-gray-800'}`}
                          style={{ height: 40, padding: '0 12px' }}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          disabled={disabled}
                          value={row.productTemperature ?? ''}
                          onChange={(e) => setProductTableRow(id, { ...row, productTemperature: e.target.value })}
                          className={`w-full min-w-[70px] rounded-[10px] border border-[#D0D5DD] bg-white focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)] ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'text-gray-800'}`}
                          style={{ height: 40, padding: '0 12px' }}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min="1"
                          disabled={disabled}
                          value={row.invoiceQuantity === 0 ? '' : row.invoiceQuantity}
                          onChange={(e) => setProductTableRow(id, { ...row, invoiceQuantity: parseInt(e.target.value, 10) || 0 })}
                          className={`w-full min-w-[70px] rounded-[10px] border border-[#D0D5DD] bg-white focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)] ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'text-gray-800'}`}
                          style={{ height: 40, padding: '0 12px' }}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min="0"
                          disabled={disabled}
                          value={row.receivedQuantity === 0 ? '' : row.receivedQuantity}
                          onChange={(e) => setProductTableRow(id, { ...row, receivedQuantity: parseInt(e.target.value, 10) || 0 })}
                          className={`w-full min-w-[70px] rounded-[10px] border border-[#D0D5DD] bg-white focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)] ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'text-gray-800'}`}
                          style={{ height: 40, padding: '0 12px' }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
          {!productUploadPreviewOpen && products?.length === 0 && <p className="text-sm text-gray-500 mt-2">No products available. Add products first.</p>}
        </div>
        <div
          style={{
            border: '1px solid #E4E7EC',
            borderRadius: 14,
            backgroundColor: '#FFFFFF',
            padding: 18,
            marginTop: 18,
          }}
        >
          <h3 style={{ fontSize: 16, fontWeight: 600, color: '#101828', margin: '0 0 16px 0' }}>
            {batchesMatchReceived
              ? `Batches complete (${totalBatchQty}/${receivedQty}) — click Create to submit`
              : `Add Batch Details — Total: ${totalBatchQty}/${receivedQty}${receivedQty > totalBatchQty ? ` (add ${receivedQty - totalBatchQty} more)` : ''}`}
          </h3>
          {showAddBatchForm && (
          <>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Batch Number</label>
              <input
                type="text"
                name="batchNumber"
                value={currentBatch.batchNumber}
                onChange={handleBatchChange}
                className="w-full rounded-[10px] border border-[#D0D5DD] bg-white px-3 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                style={{ height: 44 }}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Mfg Date</label>
              <input
                type="date"
                name="manufacturingDate"
                value={currentBatch.manufacturingDate}
                onChange={handleBatchChange}
                className="w-full rounded-[10px] border border-[#D0D5DD] bg-white px-3 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                style={{ height: 44 }}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Exp Date</label>
              <input
                type="date"
                name="expiryDate"
                value={currentBatch.expiryDate}
                onChange={handleBatchChange}
                className="w-full rounded-[10px] border border-[#D0D5DD] bg-white px-3 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                style={{ height: 44 }}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Quantity</label>
              <input
                type="number"
                name="quantity"
                value={currentBatch.quantity}
                onChange={handleBatchChange}
                min="0"
                className="w-full rounded-[10px] border border-[#D0D5DD] bg-white px-3 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                style={{ height: 44 }}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Condition</label>
              <select
                name="condition"
                value={currentBatch.condition}
                onChange={handleBatchChange}
                className="w-full rounded-[10px] border border-[#D0D5DD] bg-white px-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[rgba(24,135,66,0.12)] appearance-none pr-10"
                style={{ height: 44 }}
              >
                <option value="GOOD">GOOD</option>
                <option value="DAMAGED">DAMAGED</option>
              </select>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-end gap-4">
            <div className="min-w-[200px] flex-1 max-w-[280px]">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Select Bin <span className="text-red-500">*</span>
              </label>
              <select
                name="binId"
                value={currentBatch.binId}
                onChange={handleBatchChange}
                required
                className="w-full rounded-[10px] border border-[#D0D5DD] bg-white px-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[rgba(24,135,66,0.12)] appearance-none pr-10"
                style={{ height: 44 }}
              >
                <option value="">-- Choose Storage Bin --</option>
                {(binCapacityList.length ? binCapacityList : bins)
                  .filter((bin) => {
                    const binType = bin.binType;
                    if (currentBatch.condition === 'DAMAGED') return binType === 'HOLD';
                    if (currentBatch.condition === 'GOOD') return binType === 'GOOD';
                    return true;
                  })
                  .map((bin) => {
                    const binId = bin.binId ?? bin._id;
                    const avail = Number(bin.availableCapacity ?? Math.max(0, (bin.capacity ?? 0) - (bin.currentQuantity ?? 0)));
                    const qty = Number(currentBatch.quantity || 0);
                    const disabled = qty > 0 ? avail < qty : false;
                    const capacity = Number(bin.capacity ?? 0);
                    const label = bin.availableCapacity != null
                      ? `${bin.binCode} (Avail: ${avail}/${capacity})${disabled ? ' - Not enough space' : ''}`
                      : `${bin.binCode} (${bin.currentQuantity ?? 0}/${capacity})`;
                    return (
                      <option key={binId} value={binId} disabled={disabled} className={disabled ? 'text-gray-400 bg-gray-100' : ''}>
                        {label}
                      </option>
                    );
                  })}
              </select>
            </div>
            <button
              type="button"
              className="rounded-[10px] border-[1.5px] border-[#188742] bg-white text-[#188742] font-semibold transition hover:bg-[#188742]/5"
              style={{ height: 44, paddingLeft: 26, paddingRight: 26 }}
              onClick={addBatch}
            >
              Add Batch
            </button>
          </div>
          </>
          )}
          {!showAddBatchForm && (formData.batches || []).length > 0 && totalBatchQty > receivedQty && (
            <p className="text-sm text-amber-600 font-medium mt-2">
              Total: {totalBatchQty}/{receivedQty} — Total batch quantity must match received quantity
            </p>
          )}
        </div>

        {(formData.batches || []).length > 0 && (
          <div
            style={{
              border: '2px dashed rgba(24,135,66,0.55)',
              borderRadius: 14,
              backgroundColor: 'rgba(24,135,66,0.04)',
              padding: 16,
              marginTop: 18,
            }}
          >
            <div className="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-[#188742]" aria-hidden>
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
              </svg>
              <span style={{ fontWeight: 600, color: '#101828' }}>
                Batches complete ({totalBatchQty}/{receivedQty})
              </span>
            </div>
            <p className="mt-1 text-[14px]" style={{ color: '#344054' }}>
              Added Batches : {(formData.batches || []).length}
            </p>
            <div
              className="overflow-x-auto"
              style={{ marginTop: 12, border: '1px solid #EEF1F5', borderRadius: 14, padding: 14, backgroundColor: '#fff' }}
            >
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th className="px-3 text-left font-semibold rounded-l-[12px]" style={{ height: 52, backgroundColor: '#F6F7FA', color: '#98A2B3' }}>Mfg Date</th>
                    <th className="px-3 text-left font-semibold" style={{ backgroundColor: '#F6F7FA', color: '#98A2B3' }}>Exp Date</th>
                    <th className="px-3 text-left font-semibold" style={{ backgroundColor: '#F6F7FA', color: '#98A2B3' }}>Qty</th>
                    <th className="px-3 text-left font-semibold" style={{ backgroundColor: '#F6F7FA', color: '#98A2B3' }}>Condition</th>
                    <th className="px-3 text-left font-semibold" style={{ backgroundColor: '#F6F7FA', color: '#98A2B3' }}>Bin</th>
                    {!editingTransactionId && (
                      <th className="px-3 text-left font-semibold rounded-r-[12px]" style={{ backgroundColor: '#F6F7FA', color: '#98A2B3' }}>Action</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {(formData.batches || []).map((batch, index) => (
                    <tr key={index} className="border-b border-gray-100 align-top">
                      <td className="px-3 py-2">
                        {editingTransactionId ? (
                          <input
                            type="date"
                            value={batch.manufacturingDate || ''}
                            onChange={(e) => updateBatchField(index, 'manufacturingDate', e.target.value)}
                            className="w-full min-w-[120px] rounded-[10px] border border-[#D0D5DD] bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                            style={{ height: 40 }}
                          />
                        ) : (
                          <span className="text-sm" style={{ color: '#101828' }}>
                            {batch.manufacturingDate ? new Date(batch.manufacturingDate).toLocaleDateString() : 'N/A'}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {editingTransactionId ? (
                          <input
                            type="date"
                            value={batch.expiryDate || ''}
                            onChange={(e) => updateBatchField(index, 'expiryDate', e.target.value)}
                            className="w-full min-w-[120px] rounded-[10px] border border-[#D0D5DD] bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                            style={{ height: 40 }}
                          />
                        ) : (
                          <span className="text-sm" style={{ color: '#101828' }}>
                            {batch.expiryDate ? new Date(batch.expiryDate).toLocaleDateString() : 'N/A'}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {editingTransactionId ? (
                          <input
                            type="number"
                            min="0"
                            value={batch.quantity ?? ''}
                            onChange={(e) => updateBatchField(index, 'quantity', e.target.value)}
                            className="w-full min-w-[80px] rounded-[10px] border border-[#D0D5DD] bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                            style={{ height: 40 }}
                          />
                        ) : (
                          <span className="text-sm font-medium" style={{ color: '#101828' }}>{batch.quantity || 0}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {editingTransactionId ? (
                          <select
                            value={batch.condition || 'GOOD'}
                            onChange={(e) => updateBatchField(index, 'condition', e.target.value)}
                            className="w-full min-w-[100px] rounded-[10px] border border-[#D0D5DD] bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                            style={{ height: 40 }}
                          >
                            <option value="GOOD">GOOD</option>
                            <option value="DAMAGED">DAMAGED</option>
                          </select>
                        ) : (
                          <span className="text-sm" style={{ color: '#101828' }}>{batch.condition || 'GOOD'}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {editingTransactionId ? (
                          <select
                            value={batch.binId || ''}
                            onChange={(e) => updateBatchField(index, 'binId', e.target.value)}
                            className="w-full min-w-[140px] rounded-[10px] border border-[#D0D5DD] bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                            style={{ height: 40 }}
                          >
                            <option value="">-- Bin --</option>
                            {(binCapacityList.length ? binCapacityList : bins)
                              .filter((bin) => {
                                const batchCondition = batch.condition || 'GOOD';
                                const binType = bin.binType;
                                if (batchCondition === 'DAMAGED') return binType === 'HOLD';
                                if (batchCondition === 'GOOD') return binType === 'GOOD';
                                return true;
                              })
                              .map((bin) => {
                                const binId = bin.binId ?? bin._id;
                                const avail = Number(bin.availableCapacity ?? Math.max(0, (bin.capacity ?? 0) - (bin.currentQuantity ?? 0)));
                                const qty = Number(batch.quantity || 0);
                                const disabled = qty > 0 ? avail < qty : false;
                                const capacity = Number(bin.capacity ?? 0);
                                const label = bin.availableCapacity != null
                                  ? `${bin.binCode} (Avail: ${avail}/${capacity})${disabled ? ' - Not enough space' : ''}`
                                  : `${bin.binCode} (${bin.currentQuantity ?? 0}/${capacity})`;
                                return (
                                  <option key={binId} value={binId} disabled={disabled} className={disabled ? 'text-gray-400 bg-gray-100' : ''}>
                                    {label}
                                  </option>
                                );
                              })}
                          </select>
                        ) : (
                          <span className="text-sm" style={{ color: '#101828' }}>
                            {(binCapacityList.find(b => String(b.binId || b._id) === String(batch.binId)) || bins.find(b => b._id === batch.binId))?.binCode || 'N/A'}
                          </span>
                        )}
                      </td>
                      {!editingTransactionId && (
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => removeBatch(index)}
                            className="inline-flex items-center justify-center rounded-lg border border-red-300 bg-white p-2 text-red-600 transition hover:bg-red-50"
                            title="Remove"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                            </svg>
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </FormPopup>
      <InwardDetailsModal
        open={viewOpen}
        onClose={() => {
          setViewOpen(false);
          setViewData(null);
          setViewError(null);
        }}
        loading={viewLoading}
        error={viewError}
        data={viewData}
        onRetry={() => viewRowRef.current && openView(viewRowRef.current)}
      />
      <Table
        columns={inwardColumns}
        data={transactions}
        loading={loading}
        emptyMessage="No inward transactions found"
        actions={inwardActions}
        onRowClick={(row) => openView(row)}
      />
    </div>
  );
}
